import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, Sparkles, Trophy, Zap, Timer, Ticket, Flame } from "lucide-react";
import confetti from "canvas-confetti";
import api from "../lib/api";

// ---- Audio SFX (real audio files + Web Audio fallback for instant clicks) ----
const SFX_FILES = {
  right: "/sounds/correct.mp3",
  wrong: "/sounds/wrong.mp3",
  combo: "/sounds/combo.mp3",
  win: "/sounds/win.mp3",
};

function useSFX() {
  const poolsRef = useRef(null);
  const ctxRef = useRef(null);
  const ctx = () => (ctxRef.current ||= new (window.AudioContext || window.webkitAudioContext)());

  // Lazy-init an audio pool (2 elements per sound) for rapid replay
  const getPool = (key) => {
    if (!poolsRef.current) poolsRef.current = {};
    if (!poolsRef.current[key]) {
      const a1 = new Audio(SFX_FILES[key]); a1.preload = "auto"; a1.volume = 0.75;
      const a2 = new Audio(SFX_FILES[key]); a2.preload = "auto"; a2.volume = 0.75;
      poolsRef.current[key] = [a1, a2, 0];
    }
    return poolsRef.current[key];
  };

  const play = (key) => {
    try {
      const pool = getPool(key);
      const idx = pool[2] % 2;
      const el = pool[idx];
      el.currentTime = 0;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
      pool[2] = idx + 1;
    } catch {}
  };

  const beep = (freq, dur = 0.06, type = "square", vol = 0.08) => {
    try {
      const c = ctx(); const o = c.createOscillator(); const g = c.createGain();
      o.type = type; o.frequency.value = freq; g.gain.value = vol;
      o.connect(g); g.connect(c.destination); o.start();
      g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
      o.stop(c.currentTime + dur);
    } catch {}
  };

  return {
    tick: (fast) => beep(fast ? 1600 : 900, 0.035, "square", fast ? 0.12 : 0.05),
    click: () => beep(2200, 0.02, "square", 0.06),
    right: () => play("right"),
    combo: () => play("combo"),
    wrong: () => play("wrong"),
    win: () => play("win"),
    slot: () => { for (let i = 0; i < 10; i++) setTimeout(() => beep(500 + i * 80, 0.03, "square", 0.05), i * 45); },
  };
}

// ---- Helpers ----
const TIME_S = 12;
const MAX_POINTS = 1000;
const AUTO_ADVANCE_MS = 1900;

const CORRECT_REACTIONS = [
  { emoji: "🎉", msg: "BOOM! Che genio!" },
  { emoji: "🔥", msg: "Sei in fiamme!" },
  { emoji: "🚀", msg: "Colpo perfetto!" },
  { emoji: "🧠", msg: "Cervellone attivato!" },
  { emoji: "⭐", msg: "Stellare!" },
  { emoji: "💎", msg: "Diamante puro!" },
];
const WRONG_REACTIONS = [
  { emoji: "😅", msg: "Nemmeno tirando a indovinare..." },
  { emoji: "🧠💤", msg: "Il cervello ha fatto un riavvio rapido?" },
  { emoji: "💪", msg: "L'importante è parteci... no, scherzo! Riprenditi!" },
  { emoji: "🙈", msg: "Ops... facciamo finta di niente!" },
  { emoji: "🌀", msg: "Un piccolo scivolone, si recupera!" },
];
const COMBO_LABELS = {
  3: { text: "COMBO x3! 💥", color: "from-[#D99B26] to-[#C85A32]" },
  5: { text: "SEI IN FIAMME! 🔥", color: "from-[#E05328] to-[#C85A32]" },
  7: { text: "INARRESTABILE! ⚡", color: "from-[#3B503A] to-[#D99B26]" },
  10: { text: "LEGGENDA! 👑", color: "from-[#D99B26] to-[#E05328]" },
};
const BADGES = [
  { min: 800, name: "Il Cecchino della Serata", emoji: "🎯" },
  { min: 500, name: "Cervellone in Formazione", emoji: "🧠" },
  { min: 300, name: "Recupero del Secolo", emoji: "🔥" },
  { min: 0, name: "Sfidante Coraggioso", emoji: "🎮" },
];

function vibrate(pattern) { try { navigator.vibrate?.(pattern); } catch {} }

function fireConfetti() {
  try {
    const defaults = { origin: { y: 0.6 }, colors: ["#D99B26", "#C85A32", "#3B503A", "#FBF9F5"] };
    confetti({ ...defaults, particleCount: 80, spread: 70, startVelocity: 45 });
    setTimeout(() => confetti({ ...defaults, particleCount: 60, spread: 100, angle: 60, origin: { x: 0, y: 0.7 } }), 150);
    setTimeout(() => confetti({ ...defaults, particleCount: 60, spread: 100, angle: 120, origin: { x: 1, y: 0.7 } }), 250);
  } catch {}
}

// ---- Animated slot-machine counter ----
function SlotCounter({ target, duration = 1400 }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = null; let raf = 0;
    const tick = (ts) => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return <span className="font-mono tabular-nums">{val}</span>;
}

// ---- Floating +points indicator ----
function FloatingPoints({ points, id }) {
  return (
    <div key={id} className="absolute top-16 left-1/2 -translate-x-1/2 text-4xl font-black text-[#D99B26] float-up" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>
      +{points}
    </div>
  );
}

export default function QuizTournament({ slug, guest, onClose }) {
  const [meta, setMeta] = useState(null);
  const [matchIdx, setMatchIdx] = useState(0);
  const [qInMatch, setQInMatch] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(TIME_S);
  const [points, setPoints] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [comboLabel, setComboLabel] = useState(null);
  const [reaction, setReaction] = useState(null); // {emoji, msg, ok}
  const [floaters, setFloaters] = useState([]); // {id, points}
  const [shake, setShake] = useState(false);
  const [phase, setPhase] = useState("play"); // play | between | done
  const [board, setBoard] = useState([]);
  const [rank, setRank] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [reviewUrl, setReviewUrl] = useState(null);

  const timerRef = useRef(null); const startTsRef = useRef(0); const advTimerRef = useRef(null);
  const sfx = useSFX();

  useEffect(() => { api.get(`/public/menu/${slug}/quiz`).then(({ data }) => setMeta(data)); }, [slug]);

  const globalIdx = matchIdx * (meta?.per_match || 5) + qInMatch;
  const totalQuestions = (meta?.matches || 4) * (meta?.per_match || 5);
  const cur = meta && meta.questions[globalIdx];

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  const stopAdv = () => { if (advTimerRef.current) { clearTimeout(advTimerRef.current); advTimerRef.current = null; } };

  const goNext = useCallback(async () => {
    stopAdv();
    const perMatch = meta.per_match;
    if (qInMatch + 1 < perMatch) { setQInMatch((q) => q + 1); return; }
    if (matchIdx + 1 < meta.matches) {
      const { data } = await api.get(`/public/menu/${slug}/quiz/leaderboard`);
      setBoard(data.leaderboard); setPhase("between"); sfx.slot(); return;
    }
    const res = await api.post(`/public/menu/${slug}/quiz/score`, { guest_id: guest.guest_id, correct, total: totalQuestions, points });
    setRank(res.data.rank); setCoupon(res.data.coupon); setReviewUrl(res.data.review_url || null);
    const { data } = await api.get(`/public/menu/${slug}/quiz/leaderboard`);
    setBoard(data.leaderboard); setPhase("done"); sfx.win(); fireConfetti(); vibrate([100, 60, 100, 60, 200]);
  }, [meta, qInMatch, matchIdx, slug, guest, correct, totalQuestions, points, sfx]);

  const handleTimeout = useCallback(async () => {
    stopTimer(); if (selected !== null) return;
    setSelected(-1); sfx.wrong(); vibrate(120); setShake(true); setTimeout(() => setShake(false), 500);
    setStreak(0);
    const r = WRONG_REACTIONS[Math.floor(Math.random() * WRONG_REACTIONS.length)];
    setReaction({ ...r, ok: false, timedOut: true });
    const { data } = await api.post(`/public/menu/${slug}/quiz/check`, { id: cur.id, choice: -1 });
    setResult({ ...data, timedOut: true });
    advTimerRef.current = setTimeout(goNext, AUTO_ADVANCE_MS);
  }, [selected, cur, slug, sfx, goNext]);

  useEffect(() => {
    if (!cur || phase !== "play") return;
    setSelected(null); setResult(null); setReaction(null); setComboLabel(null);
    setTimeLeft(TIME_S); startTsRef.current = Date.now();
    stopTimer(); stopAdv();
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        const nxt = t - 0.1;
        if (nxt <= 0) { handleTimeout(); return 0; }
        if (Math.abs(nxt - Math.round(nxt)) < 0.05) sfx.tick(nxt < 4);
        return nxt;
      });
    }, 100);
    return () => { stopTimer(); stopAdv(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalIdx, phase]);

  useEffect(() => () => { stopTimer(); stopAdv(); }, []);

  const submit = async (choice) => {
    if (selected !== null) return;
    stopTimer(); sfx.click();
    setSelected(choice);
    const elapsed = (Date.now() - startTsRef.current) / 1000;
    const { data } = await api.post(`/public/menu/${slug}/quiz/check`, { id: cur.id, choice });
    let earned = 0;
    if (data.correct) {
      const speedFactor = Math.max(0, 1 - elapsed / TIME_S);
      earned = Math.round(MAX_POINTS * (0.3 + 0.7 * speedFactor));
      setPoints((p) => p + earned); setCorrect((c) => c + 1);
      const newStreak = streak + 1;
      setStreak(newStreak); setBestStreak((b) => Math.max(b, newStreak));
      // Floating points
      const id = Date.now();
      setFloaters((f) => [...f, { id, points: earned }]);
      setTimeout(() => setFloaters((f) => f.filter((x) => x.id !== id)), 1300);
      // SFX + haptics
      if (newStreak >= 3) { sfx.combo(); vibrate([50, 40, 50, 40, 80]); } else { sfx.right(); vibrate([40, 30, 60]); }
      fireConfetti();
      const activeCombo = newStreak >= 10 ? COMBO_LABELS[10] : newStreak >= 7 ? COMBO_LABELS[7] : newStreak >= 5 ? COMBO_LABELS[5] : newStreak >= 3 ? COMBO_LABELS[3] : null;
      if (activeCombo) setComboLabel(activeCombo);
      const r = CORRECT_REACTIONS[Math.floor(Math.random() * CORRECT_REACTIONS.length)];
      setReaction({ ...r, ok: true });
    } else {
      sfx.wrong(); vibrate(180); setShake(true); setTimeout(() => setShake(false), 500);
      setStreak(0);
      const r = WRONG_REACTIONS[Math.floor(Math.random() * WRONG_REACTIONS.length)];
      setReaction({ ...r, ok: false });
    }
    setResult({ ...data, earned });
    advTimerRef.current = setTimeout(goNext, AUTO_ADVANCE_MS);
  };

  const startNextMatch = () => { setMatchIdx((m) => m + 1); setQInMatch(0); setPhase("play"); };

  if (!meta) return <div className="fixed inset-0 bg-[#161412] z-50 grid place-items-center text-white">Caricamento...</div>;

  const bg = "bg-gradient-to-br from-[#161412] via-[#231F1D] to-[#3B503A]";
  const timePct = (timeLeft / TIME_S) * 100;
  const urgent = timeLeft < 4;
  const currentBadge = BADGES.find((b) => points >= b.min);

  return (
    <div className={`fixed inset-0 z-50 ${bg} text-white overflow-y-auto ${shake ? "shake-x" : ""}`}>
      <button data-testid="quiz-close-btn" onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur grid place-items-center z-20"><X size={20}/></button>

      {/* Floating +points */}
      <div className="relative">{floaters.map((f) => <FloatingPoints key={f.id} id={f.id} points={f.points} />)}</div>

      <div className="max-w-md mx-auto min-h-screen p-6 flex flex-col">
        {phase === "play" && cur && (<>
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-[#D99B26] font-bold flex items-center gap-2"><Sparkles size={14}/> Menuparty Tournament</div>
            <div className="mt-2 flex items-center justify-between text-xs opacity-80">
              <span>Manche {matchIdx+1}/{meta.matches} · Domanda {qInMatch+1}/{meta.per_match}</span>
              <span className="inline-flex items-center gap-2">
                {streak >= 2 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E05328]/30 text-[#D99B26] font-bold"><Flame size={11}/> x{streak}</span>}
                <span className="inline-flex items-center gap-1 font-bold text-[#D99B26]"><Zap size={12}/> {points} pt</span>
              </span>
            </div>
            <div className="mt-2 flex gap-1">{Array.from({length: meta.per_match}).map((_, idx) => <div key={idx} className={`h-1 flex-1 rounded ${idx <= qInMatch ? "bg-[#C85A32]" : "bg-white/20"}`}/>)}</div>
            <div className="mt-4 relative h-2 rounded-full bg-white/10 overflow-hidden">
              <div className={`absolute inset-y-0 left-0 transition-all ${urgent ? "bg-[#E05328]" : "bg-[#D99B26]"}`} style={{ width: `${timePct}%` }}/>
            </div>
            <div className={`mt-1 text-xs text-right font-mono ${urgent ? "text-[#E05328] font-bold animate-pulse" : "opacity-70"}`}><Timer size={10} className="inline mr-1"/>{timeLeft.toFixed(1)}s</div>
          </div>

          <h2 className="font-serif-display text-2xl font-bold mt-6 leading-tight">{cur.q}</h2>

          <div className="mt-6 space-y-3 flex-1">
            {cur.opts.map((opt, idx) => {
              const isSel = selected === idx;
              const isRight = result && idx === result.answer;
              const isWrong = isSel && result && !result.correct;
              return (
                <button key={idx} data-testid={`quiz-opt-${idx}`} onClick={() => submit(idx)} disabled={selected !== null}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${isRight ? "bg-[#3B503A] border-[#3B503A] glow-pulse" : isWrong ? "bg-[#C85A32]/40 border-[#C85A32] shake-x" : isSel ? "bg-white/20 border-white/40" : "border-white/20 hover:border-white/50 active:scale-[0.98]"}`}>
                  <span className="font-semibold">{opt}</span>
                </button>
              );
            })}
          </div>

          {/* Reaction & Combo overlay */}
          {reaction && (
            <div className="mt-4 fade-in-up">
              <div className={`relative overflow-hidden text-center py-4 rounded-2xl font-bold ${reaction.ok ? "bg-[#3B503A]/90" : "bg-[#C85A32]/50"}`}>
                <div className="text-5xl pop-in" aria-hidden>{reaction.emoji}</div>
                <div className="mt-1 text-sm px-4">{reaction.msg}</div>
                {result?.correct && <div className="mt-1 text-[#D99B26] font-black text-xl">+{result.earned} punti</div>}
                {reaction.timedOut && <div className="mt-1 text-xs opacity-80">Tempo scaduto!</div>}
              </div>
              {comboLabel && (
                <div className={`mt-3 combo-burst text-center py-3 rounded-2xl font-black text-2xl bg-gradient-to-r ${comboLabel.color} text-[#161412]`}>
                  {comboLabel.text}
                </div>
              )}
              <div className="mt-3 text-center text-xs opacity-60 font-mono">Prossima domanda in arrivo...</div>
            </div>
          )}
        </>)}

        {phase === "between" && (<>
          <div className="mt-10 text-center">
            <Trophy size={48} className="mx-auto text-[#D99B26] wobble"/>
            <div className="text-xs uppercase tracking-widest text-[#D99B26] font-bold mt-3">Fine manche {matchIdx+1}</div>
            <div className="font-serif-display text-5xl font-bold mt-1 text-[#D99B26]"><SlotCounter target={points}/></div>
            <div className="opacity-70 text-sm mt-1">{correct} corrette · Miglior streak: 🔥 {bestStreak}</div>
            {currentBadge && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-[#161412] font-black pop-in">
                <span className="text-xl">{currentBadge.emoji}</span> {currentBadge.name}
              </div>
            )}
          </div>
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-[#D99B26] font-bold mb-2">Top 5 della manche</div>
            <div className="space-y-2">{board.slice(0, 5).map((r, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-white/10">
                <div className="w-8 h-8 rounded-full bg-[#D99B26] text-[#161412] grid place-items-center text-sm font-bold">#{idx+1}</div>
                <div className="flex-1 min-w-0"><div className="font-semibold truncate">{r.guest_name}</div>{r.table_number && <div className="text-xs opacity-60">T{r.table_number}</div>}</div>
                <div className="font-bold text-[#D99B26]">{r.points||0}</div>
              </div>
            ))}</div>
          </div>
          <button data-testid="quiz-next-match-btn" onClick={startNextMatch} className="mt-6 mb-6 w-full bg-[#C85A32] rounded-full py-4 font-bold hover:brightness-110 transition">Inizia Manche {matchIdx+2} →</button>
        </>)}

        {phase === "done" && (<>
          <Trophy size={64} className="mx-auto mt-8 text-[#D99B26] wobble"/>
          <div className="text-center mt-3">
            <div className="text-xs uppercase tracking-widest text-[#D99B26] font-bold">Torneo Finito</div>
            <div className="font-serif-display text-6xl font-bold mt-1 text-[#D99B26]"><SlotCounter target={points} duration={1800}/></div>
            <div className="opacity-70">punti totali · {correct}/{totalQuestions} corrette · Miglior streak 🔥 {bestStreak}</div>
            {rank && <div className="mt-3 inline-block px-4 py-1.5 rounded-full bg-[#D99B26] text-[#161412] font-bold pop-in">Posizione #{rank}</div>}
            {currentBadge && (
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-[#161412] font-black pop-in">
                <span className="text-xl">{currentBadge.emoji}</span> {currentBadge.name}
              </div>
            )}
          </div>
          {rank === 1 && coupon && (
            <div className="mt-6 p-5 rounded-2xl bg-gradient-to-br from-[#D99B26] to-[#C85A32] text-[#161412] pop-in">
              <Ticket size={28}/>
              <div className="mt-2 text-xs uppercase tracking-widest font-bold">Hai vinto un premio!</div>
              <div className="mt-1 text-sm">Mostra questo codice alla cassa per riscattarlo:</div>
              <div data-testid="quiz-coupon-code" className="mt-3 font-mono text-2xl font-black tracking-widest bg-[#161412] text-[#D99B26] rounded-xl py-3 text-center">{coupon}</div>
              {reviewUrl && (
                <a data-testid="quiz-review-link-first" href={reviewUrl} target="_blank" rel="noopener noreferrer" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#161412] text-[#D99B26] font-black py-3 hover:brightness-125 transition">
                  ⭐ Lascia una recensione
                </a>
              )}
            </div>
          )}
          {rank !== 1 && reviewUrl && (
            <div data-testid="quiz-review-cta-nonfirst" className="mt-6 p-5 rounded-2xl bg-gradient-to-br from-[#3B503A] to-[#161412] text-white pop-in border border-[#D99B26]/40">
              <div className="text-2xl font-black leading-snug">🎉 Non sei arrivato primo, ma hai comunque vinto!</div>
              <div className="mt-2 text-sm leading-relaxed opacity-95">
                Condividi la tua esperienza lasciando una recensione, presenta il ticket e goditi il tuo meritato premio. ☕🎁
              </div>
              <a data-testid="quiz-review-link-nonfirst" href={reviewUrl} target="_blank" rel="noopener noreferrer" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-[#161412] font-black py-3 hover:brightness-110 transition">
                ⭐ Lascia una recensione
              </a>
            </div>
          )}
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-[#D99B26] font-bold mb-2">Classifica finale</div>
            <div className="space-y-2">{board.map((r, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-white/10">
                <div className={`w-8 h-8 rounded-full grid place-items-center text-sm font-bold ${idx<3 ? "bg-[#D99B26] text-[#161412]" : "bg-white/20"}`}>#{idx+1}</div>
                <div className="flex-1 min-w-0"><div className="font-semibold truncate">{r.guest_name}</div>{r.table_number && <div className="text-xs opacity-60">T{r.table_number}</div>}</div>
                <div className="font-bold text-[#D99B26]">{r.points||0}</div>
              </div>
            ))}</div>
          </div>
          <button onClick={onClose} className="mt-6 mb-6 w-full bg-white/20 rounded-full py-3 font-semibold">Chiudi</button>
        </>)}
      </div>
    </div>
  );
}
